// Clients (DESIGN §7 rule 1): /clients?status=all|active|paused|completed
// Compact table Client | Owner | Current Month | Current Batch | Contract | Status; one primary Add Client. A row opens the
// client workspace (/clients/:id).
import './clients.css';
import { useMemo } from 'preact/hooks';
import { useStore } from '../store.js';
import { clientHref, setQuery } from '../router.js';
import { Page } from '../components/page.jsx';
import { FilterButtons } from '../components/filter-buttons.jsx';
import { openAddClient } from '../components/forms.jsx';
import { Avatar, Button, EmptyState, StatusBadge, cx } from '../components/ui.jsx';
import { batchLabel, monthLabel } from '../../shared/constants.js';
import { stageProgressText } from '../../shared/derive.js';
import { formatDate } from '../../shared/dates.js';
import { compareClients } from './workspace.jsx';
import { currentBatchOf, nowMonthOf } from './client.jsx';
import { clientContract } from '../contract.js';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
];

// One row's facts: { client, owner, nowMonth, off, batch, batchMonth }.
function rowOf(state, index, client, today) {
  const nowMonth = nowMonthOf(client, today);
  const month = nowMonth ? (index.monthsByClient.get(client.id) || []).find((m) => m.monthNumber === nowMonth) : null;
  const batch = currentBatchOf(index, client.id);
  return {
    client,
    owner: client.owner ? index.teamById.get(client.owner) : null,
    nowMonth,
    off: month?.off === true,
    batch,
    batchMonth: batch ? index.monthsById.get(batch.monthId)?.monthNumber || 0 : 0,
    contract: clientContract(state, index, client, today),
  };
}

export function ClientsView({ route }) {
  const { state, index, today } = useStore();
  const status = STATUS_FILTERS.some((f) => f.value === route.query.status) ? route.query.status : 'all';
  const rows = useMemo(
    () => [...state.clients]
      .filter((c) => status === 'all' || c.status.toLowerCase() === status)
      .sort(compareClients)
      .map((c) => rowOf(state, index, c, today)),
    [state, index, today, status],
  );

  const addClient = <Button variant="primary" icon="plus" onClick={() => openAddClient()}>Add Client</Button>;
  // No clients yet: nothing to filter — the empty state carries the one button.
  if (!state.clients.length) {
    return (
      <Page title="Clients">
        <EmptyState title="No clients yet." description="Add your first client — it starts at Month 01." action={addClient} />
      </Page>
    );
  }
  return (
    <Page title="Clients" meta={state.clients.length} actions={addClient}>
      <div class="toolbar cls-toolbar">
        <FilterButtons
          label="Client status"
          options={STATUS_FILTERS}
          value={status}
          onChange={(v) => setQuery({ status: v === 'all' ? '' : v })}
        />
      </div>
      {rows.length ? (
        <div class="table cls-table" role="table" aria-label="Clients">
          <div class="table-row table-head cls-row" role="row">
            <span role="columnheader">Client</span>
            <span role="columnheader">Owner</span>
            <span role="columnheader">Current Month</span>
            <span role="columnheader">Current Batch</span>
            <span role="columnheader">Contract</span>
            <span role="columnheader">Status</span>
          </div>
          {rows.map((r) => <ClientRow key={r.client.id} row={r} today={today} />)}
        </div>
      ) : (
        <EmptyState title="No clients with this status." />
      )}
    </Page>
  );
}

function ClientRow({ row, today }) {
  const { client, owner, nowMonth, off, batch, batchMonth, contract } = row;
  const starts = !nowMonth && client.startDate && client.startDate > today;
  return (
    <a class={cx('table-row cls-row', off && 'is-off')} href={clientHref(client.id)} role="row">
      <span class="cls-name" role="cell">
        <span class="truncate">{client.name}</span>
        {(client.contactName || client.instagram) && <span class="cls-sub truncate">{client.contactName || `@${client.instagram}`}</span>}
      </span>
      <span class="cls-owner" role="cell">
        <Avatar name={owner?.name} size={20} />
        <span class="truncate">{owner ? owner.name.split(/\s+/)[0] : 'Unassigned'}</span>
      </span>
      <span class="cls-month num" role="cell">
        {starts ? <span class="cell-muted">Starts {formatDate(client.startDate, 'short')}</span> : monthLabel(nowMonth || 1)}
        {off && <span class="cls-off">Off</span>}
      </span>
      <span class="cls-batch" role="cell">
        {batch ? (
          <>
            <span class="num">{batchLabel(batch.batchNumber)}{batchMonth ? <span class="cell-muted"> · {monthLabel(batchMonth)}</span> : null}</span>
            <span class="cls-sub truncate">{stageProgressText(batch)}</span>
          </>
        ) : <span class="faint">—</span>}
      </span>
      <span class="cls-contract num" role="cell" title={contract?.text}>
        {contract && (
          <>
            {/* 768–1279px: "B 5/12" / "M 7/12" (the full text is the cell's tooltip) */}
            <span><span class="cls-short">B </span>{contract.batch}<span class="cls-unit"> / {contract.batches} {contract.batches === 1 ? 'batch' : 'batches'}</span><span class="cls-short">/{contract.batches}</span></span>
            <span class={cx('cls-sub', contract.month > contract.endMonth && 'is-over')}>
              <span class="cls-short">M </span>{contract.month}<span class="cls-unit"> / {contract.months} months{contract.off ? ` (+${contract.off} off)` : ''}</span><span class="cls-short">/{contract.months}</span>
              {contract.off ? <span class="cls-offn"> (+{contract.off} off)</span> : null}
            </span>
          </>
        )}
      </span>
      <span class="cls-status" role="cell"><StatusBadge status={client.status} kind="client" /></span>
    </a>
  );
}
